"use client";

import { OfferAwareLink } from "@/app/[lang]/(landing)/offer-utils/OfferAwareLink";
import { getBaseUrl } from "@/components/Link/utils";
import { BannersController } from "@/components/discounts/BannersController";
import {
  Default,
  DualColumn,
  DualColumnBenefits,
  Thin,
} from "@/components/ui/Hero";
import type { ThinHeroAlign } from "@/components/ui/Hero/Thin";
import {
  benefitsCopyComponents,
  buttonsActionCopyComponents,
  defaultComponents,
  dualColumnComponents,
  thinComponents,
} from "@/components/ui/Hero/richTextComponents";
import { sliceAnchor } from "@/utils/prismic";
import { Button, ButtonColors, ButtonProps } from "@cgc/ui/button";
import type { Content } from "@prismicio/client";
import * as prismicH from "@prismicio/helpers";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import React from "react";

export type HeroSliceProps = SliceComponentProps<Content.HeroSlice>;

const DualHeroButton: React.FC<ButtonProps> = (props) => (
  <Button
    {...props}
    color="secondary"
    className="min-w-[150px] sm:w-full md:min-w-[155px]"
    typoSize="sub3"
  />
);

const ThinHeroButton: React.FC<ButtonProps & { buttonColor?: string }> = ({
  buttonColor,
  ...props
}) => (
  <Button
    {...props}
    color={(buttonColor?.toLowerCase() ?? "secondary") as ButtonColors}
    className="min-w-[150px] sm:w-full md:min-w-[155px]"
    typoSize="sub3"
  />
);

const HeroSlice = ({ slice }: HeroSliceProps) => {
  const queryParams = useSearchParams();

  const appendQueryParams = (url: string) => {
    const urlObj = new URL(url, getBaseUrl());

    const params = new URLSearchParams(queryParams.toString());
    params.forEach((value, key) => {
      if (urlObj.searchParams.has(key)) {
        urlObj.searchParams.set(key, value);
      } else {
        urlObj.searchParams.append(key, value);
      }
    });

    return urlObj.toString();
  };

  const backgroundColor = prismicH.isFilled.color(
    slice.primary.background_color,
  )
    ? slice.primary.background_color
    : undefined;

  const commonProps = {
    id: sliceAnchor(slice),
    copy: (
      <PrismicRichText
        field={slice.primary.text}
        components={defaultComponents}
      />
    ),
    backgroundImage: prismicH.asImageSrc(slice.primary.backgroundImage)!,
    backgroundColor,
    fluid: slice.primary.fullwidth,
    hasCopy: prismicH.isFilled.richText(slice.primary.text),
  };

  if (slice.variation == "default") {
    return (
      <Default
        {...commonProps}
        buttons={
          prismicH.isFilled.link(slice.primary.button_link)
            ? [
                <OfferAwareLink
                  key={`${slice.variation}:button:${prismicH.asLink(
                    slice.primary.button_link,
                  )}:second`}
                  url={appendQueryParams(
                    prismicH.asLink(slice.primary.button_link)!,
                  )}
                >
                  <Button>{slice.primary.button_copy || "Learn More"}</Button>
                </OfferAwareLink>,
              ]
            : []
        }
      />
    );
  }
  if (
    slice.variation == "twoColumns" ||
    slice.variation == "dualColumnBenefits"
  ) {
    const buttons = [
      ...(prismicH.isFilled.link(slice.primary.button_link)
        ? [
            <OfferAwareLink
              url={appendQueryParams(
                prismicH.asLink(slice.primary.button_link)!,
              )}
              key={`${slice.variation}:button:${prismicH.asLink(
                slice.primary.button_link,
              )}`}
            >
              <DualHeroButton>
                {slice.primary.button_copy || "Learn More"}
              </DualHeroButton>
            </OfferAwareLink>,
          ]
        : []),
      ...(prismicH.isFilled.link(slice.primary.second_button_link)
        ? [
            <OfferAwareLink
              url={appendQueryParams(
                prismicH.asLink(slice.primary.second_button_link)!,
              )}
              key={`${slice.variation}:button:${prismicH.asLink(
                slice.primary.second_button_link,
              )}:second`}
            >
              <DualHeroButton>
                {slice.primary.second_button_copy || "Learn More"}
              </DualHeroButton>
            </OfferAwareLink>,
          ]
        : []),
    ];
    if (slice.variation == "twoColumns") {
      return (
        <DualColumn
          {...commonProps}
          copy={
            <PrismicRichText
              field={slice.primary.text}
              components={dualColumnComponents}
            />
          }
          imageLeft={slice.primary.image_left}
          buttonsActionCopy={
            <PrismicRichText
              field={slice.primary.button_action_copy}
              components={buttonsActionCopyComponents}
            />
          }
          buttons={buttons}
          bottomCopy={
            prismicH.isFilled.richText(slice.primary.bottom_copy) ? (
              <PrismicRichText
                field={slice.primary.bottom_copy}
                components={buttonsActionCopyComponents}
              />
            ) : undefined
          }
        />
      );
    }
    if (slice.variation == "dualColumnBenefits") {
      return (
        <DualColumnBenefits
          {...commonProps}
          copy={
            <PrismicRichText
              field={slice.primary.text}
              components={dualColumnComponents}
            />
          }
          imageLeft={slice.primary.image_left}
          buttonsActionCopy={
            <PrismicRichText
              field={slice.primary.button_action_copy}
              components={buttonsActionCopyComponents}
            />
          }
          buttons={buttons}
          benefits={slice.items.slice(0, 3).map((item) => ({
            copy: (
              <PrismicRichText
                field={item.benefit_copy!}
                components={benefitsCopyComponents}
              />
            ),
            image: prismicH.asImageSrc(item.benefit_image!),
          }))}
          bottomCopy={
            prismicH.isFilled.richText(slice.primary.bottom_copy) ? (
              <PrismicRichText
                field={slice.primary.bottom_copy}
                components={buttonsActionCopyComponents}
              />
            ) : undefined
          }
        />
      );
    }
  }

  if (slice.variation == "thin") {
    return (
      <Thin
        {...commonProps}
        copy={
          <PrismicRichText
            field={slice.primary.text}
            components={thinComponents}
          />
        }
        buttons={
          prismicH.isFilled.link(slice.primary.button_link)
            ? [
                <Link
                  href={appendQueryParams(
                    prismicH.asLink(slice.primary.button_link)!,
                  )}
                  key={`${slice.variation}:button:${prismicH.asLink(
                    slice.primary.button_link,
                  )}`}
                >
                  <ThinHeroButton buttonColor={slice.primary.button_color}>
                    {slice.primary.button_copy || "Learn More"}
                  </ThinHeroButton>
                </Link>,
              ]
            : []
        }
        align={
          (slice.primary.align?.toLowerCase() ?? "centered") as ThinHeroAlign
        }
      />
    );
  }

  return null;
};

export default function HeroSliceWrapper(props: HeroSliceProps) {
  const { bannerController } = props.context as {
    bannerController: { page?: string };
  };

  return (
    <>
      <HeroSlice {...props} />
      {bannerController && bannerController?.page && (
        <BannersController page={bannerController.page} lang={"en-us"} />
      )}
    </>
  );
}
